import { useState } from "react";
import Modal from "../../../components/modals/Modal";
import { IconClock, IconEdit, IconMapPin, IconPlus, IconTrash } from "../../../components/icons/IconsMedico";
import { EmptyState } from "../../../components/utils/Utils";
import { apiClient } from "../../../api/apiClient";
import { DIA_SEMANA_UI_A_BACKEND, toBackendTime, getEntityId } from "./medicoHelpers";
import "./Disponibilidad.css";

const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const sedeNombre = (sedeId, sedes) => sedes.find((s) => s.id === sedeId)?.nombre || "Sede sin asignar";

const formVacio = (sedes) => ({
    diaSemana: "Lunes",
    horaDesde: "08:00",
    horaHasta: "12:00",
    sede: sedes[0]?.id || "",
});

const Disponibilidad = ({ disponibilidades = [], sedes = [], loading, error, medico, onDisponibilidadModificada }) => {
    const [modal, setModal] = useState(null); // null | "crear" | item (editar)
    const [form, setForm] = useState(formVacio(sedes));
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [accionError, setAccionError] = useState("");

    const abrirCrear = () => {
        setAccionError("");
        setForm(formVacio(sedes));
        setModal("crear");
    };

    const abrirEditar = (item) => {
        setAccionError("");
        setForm({
            diaSemana: item.diaSemana,
            horaDesde: (item.horaDesde || "").slice(0, 5),
            horaHasta: (item.horaHasta || "").slice(0, 5),
            sede: item.sedeId,
        });
        setModal(item);
    };

    const guardar = async () => {
        setGuardando(true);
        setAccionError("");

        try {
            if (!medico) throw new Error("No hay un médico asignado.");
            const medicoId = getEntityId(medico);

            const payload = {
                diaSemana: DIA_SEMANA_UI_A_BACKEND[form.diaSemana] || form.diaSemana,
                horaDesde: toBackendTime(form.horaDesde),
                horaHasta: toBackendTime(form.horaHasta),
                sede: form.sede,
            };

            if (modal === "crear") {
                await apiClient.post(`/medicos/${medicoId}/disponibilidades`, payload);
            } else {
                payload.id = modal.id;
                await apiClient.patch(`/medicos/${medicoId}/disponibilidades/${modal.id}`, payload);
            }
            onDisponibilidadModificada?.();
            setModal(null);
        } catch (requestError) {
            setAccionError(requestError.message || "Error al guardar la disponibilidad.");
        } finally {
            setGuardando(false);
        }
    };

    const eliminar = async (item) => {
        setAccionError("");

        try {
            if (!medico) throw new Error("No hay un médico asignado.");
            const medicoId = getEntityId(medico);

            const payload = {
                id: item.id,
                diaSemana: DIA_SEMANA_UI_A_BACKEND[item.diaSemana] || item.diaSemana,
                horaDesde: toBackendTime(item.horaDesde),
                horaHasta: toBackendTime(item.horaHasta),
                sede: item.sedeId,
            };

            await apiClient.delete(`/medicos/${medicoId}/disponibilidades/${item.id}`, { data: payload });
            onDisponibilidadModificada?.();
            setConfirmDelete(null);
        } catch (requestError) {
            setAccionError(requestError.message || "Error al eliminar la disponibilidad.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const porDia = DIAS_SEMANA.reduce((acc, dia) => {
        const delDia = disponibilidades.filter((d) => d.diaSemana === dia);
        if (delDia.length > 0) acc[dia] = delDia;
        return acc;
    }, {});

    return (
        <div className="medico-section">
            <div className="medico-section__header">
                <div>
                    <p className="eyebrow">Configuración</p>
                    <h2>Disponibilidad horaria</h2>
                </div>
                <button className="primary-button" onClick={abrirCrear} disabled={sedes.length === 0}>
                    <IconPlus /> Agregar franja
                </button>
            </div>

            {error && <div className="patient-state patient-state--error">{error}</div>}
            {accionError && <div className="patient-state patient-state--error">{accionError}</div>}

            {loading ? (
                <div className="patient-state">
                    <div className="patient-state__loader" />
                    <p>Cargando disponibilidad...</p>
                </div>
            ) : disponibilidades.length === 0 ? (
                <EmptyState
                    icon={<IconClock />}
                    title="No tenés disponibilidades cargadas."
                    subtitle="Agregá una franja horaria para que el sistema genere turnos."
                />
            ) : (
                <div className="disponibilidad-tabla">
                    {Object.entries(porDia).map(([dia, franjas]) => (
                        <div key={dia} className="disponibilidad-dia">
                            <h3 className="disponibilidad-dia__nombre">{dia}</h3>
                            <div className="disponibilidad-dia__franjas">
                                {franjas.map((item) => (
                                    <div key={item.id} className="franja-card">
                                        <div className="franja-card__horario">
                                            <IconClock />
                                            <span>{item.horaDesde.slice(0, 5)} – {item.horaHasta.slice(0, 5)}</span>
                                        </div>
                                        <div className="franja-card__sede">
                                            <IconMapPin />
                                            <span>{sedeNombre(item.sedeId, sedes)}</span>
                                        </div>
                                        <div className="franja-card__actions">
                                            <button className="btn-icon" title="Editar" onClick={() => abrirEditar(item)}>
                                                <IconEdit />
                                            </button>
                                            <button
                                                className="btn-icon btn-icon--danger"
                                                title="Eliminar"
                                                onClick={() => setConfirmDelete(item)}
                                            >
                                                <IconTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modal !== null && (
                <Modal
                    title={modal === "crear" ? "Nueva franja horaria" : "Editar franja horaria"}
                    onClose={() => setModal(null)}
                >
                    <div className="modal-form">
                        <label className="form-field">
                            <span>Día de la semana</span>
                            <select name="diaSemana" value={form.diaSemana} onChange={handleChange}>
                                {DIAS_SEMANA.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </label>
                        <div className="modal-form__row">
                            <label className="form-field">
                                <span>Hora desde</span>
                                <input type="time" name="horaDesde" value={form.horaDesde} onChange={handleChange} />
                            </label>
                            <label className="form-field">
                                <span>Hora hasta</span>
                                <input type="time" name="horaHasta" value={form.horaHasta} onChange={handleChange} />
                            </label>
                        </div>
                        <label className="form-field">
                            <span>Sede</span>
                            <select name="sede" value={form.sede} onChange={handleChange}>
                                {sedes.map((s) => (
                                    <option key={s.id} value={s.id}>{s.nombre}</option>
                                ))}
                            </select>
                        </label>
                        <div className="modal-form__actions">
                            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
                            <button className="primary-button" onClick={guardar} disabled={guardando}>
                                {guardando ? "Guardando..." : modal === "crear" ? "Agregar" : "Guardar cambios"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {confirmDelete && (
                <Modal title="Eliminar disponibilidad" onClose={() => setConfirmDelete(null)}>
                    <div className="modal-form">
                        <p className="modal-confirm__text">
                            ¿Eliminás la franja del <strong>{confirmDelete.diaSemana}</strong> de{" "}
                            <strong>{confirmDelete.horaDesde.slice(0, 5)} a {confirmDelete.horaHasta.slice(0, 5)}</strong> en{" "}
                            <strong>{sedeNombre(confirmDelete.sedeId, sedes)}</strong>?
                        </p>
                        <div className="modal-form__actions">
                            <button className="btn-ghost" onClick={() => setConfirmDelete(null)}>Cancelar</button>
                            <button className="primary-button primary-button--danger" onClick={() => eliminar(confirmDelete)}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Disponibilidad;
